export function normalizeQuestionnaireTags(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const item of value) {
    let raw = '';
    if (typeof item === 'string') {
      raw = item;
    } else if (item && typeof item === 'object' && 'name' in item) {
      const name = (item as { name?: unknown }).name;
      if (typeof name === 'string') raw = name;
    }
    const tag = raw.trim();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    tags.push(tag);
  }
  return tags;
}

export function collectQuestionnaireTags(
  templates: ReadonlyArray<{ tags?: readonly string[] | null }>,
): string[] {
  const seen = new Set<string>();
  for (const template of templates) {
    for (const tag of template.tags ?? []) {
      const trimmed = tag.trim();
      if (trimmed) seen.add(trimmed);
    }
  }
  return Array.from(seen).sort((a, b) => a.localeCompare(b, 'da'));
}

/** Unique tags sorted by how often they appear, then Danish A–Å. */
export function collectQuestionnaireTagsByUsage(
  templates: ReadonlyArray<{ tags?: readonly string[] | null }>,
): string[] {
  const counts = new Map<string, number>();
  for (const template of templates) {
    for (const tag of template.tags ?? []) {
      const trimmed = tag.trim();
      if (!trimmed) continue;
      counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'da'))
    .map(([tag]) => tag);
}

/** Multi-select filter: a row matches when it has at least one selected tag. */
export function matchesQuestionnaireTags(
  tags: readonly string[] | null | undefined,
  selected: readonly string[],
): boolean {
  if (selected.length === 0) return true;
  const set = new Set(tags ?? []);
  return selected.some((tag) => set.has(tag));
}

export type QuestionnaireTagGroup<T> = {
  /** Stored tag. Null for questionnaires that have none. */
  tag: string | null;
  items: T[];
};

/**
 * Group questionnaires under the tags already stored on each template.
 *
 * A questionnaire with several tags is listed under each of them. Tags are
 * an equal set here: `matchesQuestionnaireTags` matches when any selected
 * tag is present, and nothing in the product marks a primary tag. Group
 * order is the Danish A–Å order from `collectQuestionnaireTags`. Order
 * inside a group follows `rows`.
 *
 * Questionnaires with no tag stay in a final group (`tag: null`). Callers
 * that render a flat list when nothing is tagged can hide that label.
 */
export function groupQuestionnairesByTag<T extends { tags?: readonly string[] | null }>(
  rows: readonly T[],
): QuestionnaireTagGroup<T>[] {
  const normalized = rows.map((row) => ({
    row,
    tags: normalizeQuestionnaireTags(row.tags),
  }));
  const tagOrder = collectQuestionnaireTags(normalized.map((item) => ({ tags: item.tags })));
  const groups: QuestionnaireTagGroup<T>[] = tagOrder.map((tag) => ({
    tag,
    items: normalized.filter((item) => item.tags.includes(tag)).map((item) => item.row),
  }));
  const untagged = normalized.filter((item) => item.tags.length === 0).map((item) => item.row);
  if (untagged.length > 0) groups.push({ tag: null, items: untagged });
  return groups;
}
